/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { BackupModel, DashboardDataModel, TierModel } from '../../../../models';
import { BaseComponent } from '../../../../../../../core/components';
import { DashboardService } from '../../../../services/component';
import { PortalRbacService } from '../../../../services/utils';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-backup-widget',
    templateUrl: './backup-page-widget.component.html',
    styleUrls: ['../portal-tier-widget.component.scss']
})
export class BackupPageWidgetComponent extends BaseComponent implements AfterViewInit {
    /**
     * Set widget position absolute, so it can sit inline with table search.
     */
    @Input() public sitInlineWithSearch: boolean = false;

    /**
     * ID of the service we are calculating for.
     */
    @Input() public serviceId: string | number;

    /**
     * View object
     */
    public view = {
        serviceCount: 0,
        tierMax: 0,
        tier: ''
    };

    /**
     * Flag to show upgrade message.
     */
    public showUpgradeMessage: boolean = false;

    /**
     * Output to parent component if the limit has been reached
     */
    @Output() public limitReached: EventEmitter<any> = new EventEmitter();

    constructor(
        private dashboardService: DashboardService,
        private portalRbacService: PortalRbacService
    ) {
        super();
    }

    ngAfterViewInit(): void {
        const data: DashboardDataModel = this.dashboardService.allData;
        this.setUpData(data);
        this.dashboardService.allDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: data => {
                    this.setUpData(data);
                }
            });
    }

    /**
     * Set up data
     * @private
     */
    private setUpData(data: DashboardDataModel): void {
        const backups: any = data?.backups;
        if (backups && this.serviceId) {
            const adcID: number = typeof this.serviceId === 'string' ? parseInt(this.serviceId, 10) : this.serviceId;
            const backupCount = backups?.filter((item: BackupModel): boolean => item.adcID === adcID).length;
            const selectedTier: TierModel = this.portalRbacService.selectedTier;
            const tierMax: number = selectedTier.backups;

            this.view = {
                serviceCount: backupCount,
                tierMax: tierMax,
                tier: selectedTier.tier
            };

            this.showUpgradeMessage = tierMax > 0 ? (tierMax - backupCount < 3) : false;
            this.limitReached.next(tierMax > 0 ? backupCount >= tierMax : false);
        }
    }
}
